import { useState, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SecurityIssuesList } from "@/components/security/SecurityIssuesList";
import { IssueSeverityCard } from "@/components/security/IssueSeverityCard";
import { IssueStatusTrendChart } from "@/components/security/IssueStatusTrendChart";
import { OpenIssuesTrendCard } from "@/components/security/OpenIssuesTrendCard";
import { SuppressionRulesSection } from "@/components/settings/SuppressionRulesSection";
import { SeverityLevel, DateRange, IssueStatus, IssueType } from "@/types/security";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { Filter, X, AlertCircle, BellOff } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useSecurityIssues, useSecurityIssueStats } from "@/hooks/useSecurityIssues";
import { useAIAgents } from "@/hooks/useAIAgents";
import { useSuppressedCount } from "@/hooks/useSuppressedCount";
import { useTenant } from "@/providers/TenantProvider";
import {
  transformToSeverityData,
  transformToOpenIssueAgeData,
  transformToStatusTrendData,
} from "@/utils/securityHelpers";

export function IssuesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab =
    searchParams.get("tab") === "suppression-rules" ? "suppression-rules" : "issues";

  const handleTabChange = (value: string) => {
    if (value === "issues") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", value);
    }
    setSearchParams(searchParams, { replace: true });
  };

  return (
    <div className="flex flex-1 flex-col gap-6 bg-muted/10 px-4 py-6 lg:px-6">
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="h-auto rounded-[15px] border bg-white p-1.5">
          <TabsTrigger className="rounded-[10px] px-4 py-2.5" value="issues">Issues</TabsTrigger>
          <TabsTrigger className="rounded-[10px] px-4 py-2.5" value="suppression-rules">Suppression Rules</TabsTrigger>
        </TabsList>
        <TabsContent value="issues">
          <IssuesTabContent />
        </TabsContent>
        <TabsContent value="suppression-rules">
          <SuppressionRulesSection />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function IssuesTabContent() {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState<DateRange>("30");
  const [page, setPage] = useState(1);
  const [severityFilter, setSeverityFilter] = useState<SeverityLevel | undefined>();
  const [statusFilter, setStatusFilter] = useState<IssueStatus | undefined>();
  const [typeFilter, setTypeFilter] = useState<IssueType | undefined>();
  const [includeSuppressed, setIncludeSuppressed] = useState(false);
  const [agentFilter, setAgentFilter] = useState<number | undefined>();
  const { selectedOrgId } = useTenant();

  const { data: suppressedCount } = useSuppressedCount();
  const { data: agents } = useAIAgents(30, selectedOrgId);

  const statsDays = parseInt(dateRange, 10);

  const {
    data: issuesResponse,
    isLoading,
    error,
  } = useSecurityIssues({
    page,
    items_per_page: 20,
    severity: severityFilter,
    status: statusFilter,
    issue_type: typeFilter,
    include_suppressed: includeSuppressed,
    agent_id: agentFilter,
    organization_id: selectedOrgId,
  });

  const {
    data: stats,
    isLoading: statsLoading,
    error: statsError,
  } = useSecurityIssueStats(statsDays, selectedOrgId);

  const issues = issuesResponse?.data ?? [];
  const totalCount = issuesResponse?.total_count ?? 0;

  // Transform stats data for UI components
  const issueSeverityData = useMemo(() => transformToSeverityData(stats), [stats]);
  const openIssueAgeData = useMemo(() => transformToOpenIssueAgeData(stats), [stats]);
  const statusTrendData = useMemo(() => transformToStatusTrendData(stats), [stats]);

  const handleIssueClick = useCallback(
    (issue: { id: string }) => {
      navigate(`/issues/${issue.id}`);
    },
    [navigate]
  );

  const handleSeverityClick = (severity: SeverityLevel) => {
    setSeverityFilter(severity);
    setPage(1); // Reset to first page when filter changes
  };

  const handleOpenIssuesClick = () => {
    setStatusFilter("OPEN");
    setSeverityFilter("CRITICAL");
    setPage(1);
  };

  const handleClearFilters = () => {
    setSeverityFilter(undefined);
    setStatusFilter(undefined);
    setTypeFilter(undefined);
    setAgentFilter(undefined);
    setIncludeSuppressed(false);
    setPage(1);
  };

  const toggleSeverityFilter = (severity: SeverityLevel) => {
    setSeverityFilter((prev) => (prev === severity ? undefined : severity));
    setPage(1);
  };

  const toggleStatusFilter = (status: IssueStatus) => {
    const newValue = statusFilter === status ? undefined : status;
    setStatusFilter(newValue);
    if (newValue === "SUPPRESSED") setIncludeSuppressed(true);
    setPage(1);
  };

  const toggleTypeFilter = (type: IssueType) => {
    setTypeFilter((prev) => (prev === type ? undefined : type));
    setPage(1);
  };

  const toggleAgentFilter = (id: number) => {
    setAgentFilter((prev) => (prev === id ? undefined : id));
    setPage(1);
  };
  const hasActiveFilters =
    severityFilter !== undefined ||
    statusFilter !== undefined ||
    typeFilter !== undefined ||
    agentFilter !== undefined ||
    includeSuppressed;
  // Loading state
  if (isLoading) {
    return (
      <div className="mt-6 space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          {[1, 2, 3].map((item) => <Skeleton key={item} className="h-[320px] rounded-[15px]" />)}
        </div>
        <Skeleton className="h-[420px] rounded-[15px]" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-1 flex-col gap-4">
        <Card className="rounded-[15px] border-destructive shadow-none">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
              <div>
                <h3 className="font-semibold text-destructive">Error</h3>
                <p className="text-sm text-muted-foreground">
                  Failed to load security issues. Please try again later.
                </p>
                {error instanceof Error && (
                  <p className="text-xs text-muted-foreground mt-1">{error.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mt-6 flex flex-1 flex-col gap-4">
      {statsError && (
        <Card className="rounded-[15px] border-yellow-500 bg-yellow-50 shadow-none dark:bg-yellow-950">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-sm text-yellow-800 dark:text-yellow-200">
              <AlertCircle className="h-4 w-4" />
              <span>
                Failed to load statistics. Some charts may show incomplete data.
                {statsError instanceof Error && ` ${statsError.message}`}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Three cards side by side */}
      <div className="grid gap-4 md:grid-cols-3">
        {statsLoading ? (
          <>
            <Card className="rounded-[15px] shadow-none">
              <CardHeader>
                <Skeleton className="h-6 w-32" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              </CardContent>
            </Card>
            <Card className="rounded-[15px] shadow-none">
              <CardHeader>
                <Skeleton className="h-6 w-40" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-[220px] w-full" />
              </CardContent>
            </Card>
            <Card className="rounded-[15px] shadow-none">
              <CardHeader>
                <Skeleton className="h-6 w-36" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          </>
        ) : (
          <>
            <IssueSeverityCard data={issueSeverityData} onSeverityClick={handleSeverityClick} />
            <IssueStatusTrendChart
              data={statusTrendData}
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
            />
            <OpenIssuesTrendCard data={openIssueAgeData} onClick={handleOpenIssuesClick} />
          </>
        )}
      </div>

      {/* Security Issues Table with Filters */}
      <section className="space-y-3">
        <div className="flex flex-row items-center justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">
              Security Issues {totalCount > 0 && `(${totalCount.toLocaleString()} total)`}
            </h2>
            {hasActiveFilters && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                {severityFilter && (
                  <Badge variant="secondary" className="flex items-center gap-1 rounded-[5px] text-xs">
                    {severityFilter}
                    <button
                      type="button"
                      aria-label={`Clear ${severityFilter} severity filter`}
                      onClick={() => toggleSeverityFilter(severityFilter)}
                      className="ml-1 h-3 w-3 cursor-pointer hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {statusFilter && (
                  <Badge variant="secondary" className="flex items-center gap-1 rounded-[5px] text-xs">
                    {statusFilter}
                    <button
                      type="button"
                      aria-label={`Clear ${statusFilter} status filter`}
                      onClick={() => toggleStatusFilter(statusFilter)}
                      className="ml-1 h-3 w-3 cursor-pointer hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {typeFilter && (
                  <Badge variant="secondary" className="flex items-center gap-1 rounded-[5px] text-xs">
                    {typeFilter}
                    <button
                      type="button"
                      aria-label={`Clear ${typeFilter} type filter`}
                      onClick={() => toggleTypeFilter(typeFilter)}
                      className="ml-1 h-3 w-3 cursor-pointer hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                {agentFilter && (
                  <Badge variant="secondary" className="flex items-center gap-1 rounded-[5px] text-xs">
                    {agents?.find((a) => a.id === agentFilter)?.name ?? `Agent #${agentFilter}`}
                    <button
                      type="button"
                      aria-label="Clear agent filter"
                      onClick={() => toggleAgentFilter(agentFilter)}
                      className="ml-1 h-3 w-3 cursor-pointer hover:opacity-70 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary rounded"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearFilters}
                  className="h-7 rounded-full px-3 text-xs"
                >
                  Clear all
                </Button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {(suppressedCount ?? 0) > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-2">
                      <Switch
                        id="show-suppressed"
                        checked={includeSuppressed}
                        onCheckedChange={(checked) => {
                          setIncludeSuppressed(checked);
                          if (!checked && statusFilter === "SUPPRESSED") setStatusFilter(undefined);
                          setPage(1);
                        }}
                        className="data-[state=checked]:bg-purple-600"
                      />
                      <label
                        htmlFor="show-suppressed"
                        className="text-xs text-muted-foreground cursor-pointer select-none flex items-center gap-1.5"
                      >
                        <BellOff className="h-3.5 w-3.5" />
                        <span>{suppressedCount} suppressed</span>
                      </label>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Toggle visibility of suppressed issues in the list</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-full bg-white">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-[20px] bg-white p-2">
                <div className="px-2 py-1.5 text-sm font-semibold">Severity</div>
                <DropdownMenuCheckboxItem
                  checked={severityFilter === "CRITICAL"}
                  onCheckedChange={() => toggleSeverityFilter("CRITICAL")}
                >
                  Critical
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={severityFilter === "HIGH"}
                  onCheckedChange={() => toggleSeverityFilter("HIGH")}
                >
                  High
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={severityFilter === "MEDIUM"}
                  onCheckedChange={() => toggleSeverityFilter("MEDIUM")}
                >
                  Medium
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={severityFilter === "LOW"}
                  onCheckedChange={() => toggleSeverityFilter("LOW")}
                >
                  Low
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-sm font-semibold">Status</div>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "OPEN"}
                  onCheckedChange={() => toggleStatusFilter("OPEN")}
                >
                  Open
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "IN_REVIEW"}
                  onCheckedChange={() => toggleStatusFilter("IN_REVIEW")}
                >
                  In Review
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "RESOLVED"}
                  onCheckedChange={() => toggleStatusFilter("RESOLVED")}
                >
                  Resolved
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "FALSE_POSITIVE"}
                  onCheckedChange={() => toggleStatusFilter("FALSE_POSITIVE")}
                >
                  False Positive
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={statusFilter === "SUPPRESSED"}
                  onCheckedChange={() => toggleStatusFilter("SUPPRESSED")}
                >
                  Suppressed
                </DropdownMenuCheckboxItem>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-sm font-semibold">Type</div>
                <DropdownMenuCheckboxItem
                  checked={typeFilter === "PII_DETECTION"}
                  onCheckedChange={() => toggleTypeFilter("PII_DETECTION")}
                >
                  PII Detection
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={typeFilter === "PROMPT_INJECTION"}
                  onCheckedChange={() => toggleTypeFilter("PROMPT_INJECTION")}
                >
                  Prompt Injection
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={typeFilter === "DATA_EXFILTRATION"}
                  onCheckedChange={() => toggleTypeFilter("DATA_EXFILTRATION")}
                >
                  Data Exfiltration
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={typeFilter === "TOXIC_CONTENT"}
                  onCheckedChange={() => toggleTypeFilter("TOXIC_CONTENT")}
                >
                  Toxic Content
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={typeFilter === "MALICIOUS_URL"}
                  onCheckedChange={() => toggleTypeFilter("MALICIOUS_URL")}
                >
                  Malicious URL
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={typeFilter === "SECRETS_EXPOSURE"}
                  onCheckedChange={() => toggleTypeFilter("SECRETS_EXPOSURE")}
                >
                  Secrets Exposure
                </DropdownMenuCheckboxItem>
                {agents && agents.length > 0 && (
                  <>
                    <DropdownMenuSeparator />
                    <div className="px-2 py-1.5 text-sm font-semibold">Agent</div>
                    {agents.map((agent) => (
                      <DropdownMenuCheckboxItem
                        key={agent.id}
                        checked={agentFilter === agent.id}
                        onCheckedChange={() => toggleAgentFilter(agent.id)}
                      >
                        {agent.name}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div>
          {issues.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <AlertCircle className="h-12 w-12 mb-4 text-muted-foreground opacity-50" />
              <p className="text-sm font-medium mb-1">No issues found</p>
              <p className="text-xs text-muted-foreground">
                {hasActiveFilters
                  ? "Try adjusting your filters"
                  : "No security issues detected yet"}
              </p>
            </div>
          ) : (
            <>
              <SecurityIssuesList issues={issues} onIssueClick={handleIssueClick} />
              {/* Pagination */}
              {totalCount > 0 && (
                <div className="mt-5 flex flex-col items-center justify-between gap-4 sm:flex-row">
                  <div className="text-xs text-muted-foreground">
                    Showing {issues.length} of {totalCount.toLocaleString()} issues
                    {totalCount > 20 && ` (Page ${page} of ${Math.ceil(totalCount / 20)})`}
                  </div>
                  {totalCount > 20 && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page === 1 || isLoading}
                        onClick={() => {
                          setPage((p) => Math.max(1, p - 1));
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="min-w-[80px] rounded-full text-xs sm:text-sm"
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={page >= Math.ceil(totalCount / 20) || isLoading}
                        onClick={() => {
                          setPage((p) => p + 1);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="min-w-[80px] rounded-full text-xs sm:text-sm"
                      >
                        Next
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
